<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">Offload Report</h3>
        <div class="text-xs-right">
          <export-excel-button :export-data="exportData"
                               v-if="$oauth.checkIfHasPermission(['export offload-report'])"></export-excel-button>
          <refresh-button :click-function="refreshData"></refresh-button>
        </div>
      </v-flex>
      <v-flex lg12>
        <v-card>
          <v-layout class="py-1 mx-2" row wrap>
            <v-flex class="pb-0" xs3>
              <v-text-field
                  :elevation="0"
                  class="hidden-sm-and-down "
                  flat
                  hide-details
                  placeholder="Search by typing here..."
                  prepend-inner-icon="search"
                  solo
                  v-model="filters.query"
                  @keypress.enter="fetchFiltersData"
              ></v-text-field>
            </v-flex>

            <v-flex xs3 class="d-flex align-center">
              <custom-date-range-picker @change="fetchFiltersData"
                                        v-model="filters.date_range"></custom-date-range-picker>
            </v-flex>
            <v-flex xs3>
              <v-autocomplete
                  :items="countries"
                  chips
                  clearable
                  deletable-chips
                  flat
                  hide-details
                  item-text="country_name"
                  item-value="id"
                  label="Country"
                  multiple
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.country"
                  @change="fetchFiltersData"
              ></v-autocomplete>
            </v-flex>

            <v-flex xs3 class="pb-0">
              <v-text-field
                  class="hidden-sm-and-down"
                  flat
                  hide-details
                  placeholder="Document Number"
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.document_number"
                  @change="fetchFiltersData"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-data-table
                :headers="headers"
                :items="data"
                :loading="loading"
                :options.sync="pagination"
                :footer-props="footerProps"
                class="elevation-0"
                v-if="$oauth.checkIfHasPermission(['read offload-report'])"
                hide-default-footer
            >
              <template v-slot:item.country="{ item }">
                <td>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                                            <span
                                                v-on="on">{{ item.country_code }}</span>
                    </template>
                    <span>{{ item.country }}</span>
                  </v-tooltip>
                </td>
              </template>

              <template v-slot:item.officer="{ item }">
                <span class="font-weight-bold" style="font-size: 13px;"> {{ item.officer }} </span>
                <br>
                <span class="" style="font-size: 12px;">{{ item.office_name }}</span>
              </template>
              <template v-slot:item.image="{ item }">
                <file-display :src="item.image"/>
              </template>
              <template v-slot:item.document="{ item }">
                <file-display :src="item.document"/>
              </template>

              <template v-slot:item.action="{ item }">
                <v-tooltip top v-if="$oauth.checkIfHasPermission('read traveller-history')">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        class="mr-2"
                        color="primary"
                        dark
                        dense
                        fab
                        v-on="on"
                        :href="'/system/travellers-history/view/'+item.traveller"
                        :to="'/system/travellers-history/view/'+item.traveller"
                        x-small>
                      <v-icon small>mdi-account</v-icon>
                    </v-btn>
                  </template>
                  <span>View Traveller</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-if="$oauth.checkIfHasPermission('print offload-report')"
                        class="mr-2"
                        color="red"
                        dark
                        fab
                        x-small
                        v-on="on"
                        @click="openPrintDialog(item)"
                    >
                      <v-icon small>mdi-file-pdf-box</v-icon>
                    </v-btn>
                  </template>
                  <span>Download PDF Report</span>
                </v-tooltip>
              </template>
            </v-data-table>
            <issued-visa-footer @nextClick="handleNextClick" @prevClick="handlePrevClick"
                                :disable-next="disableNext"
                                :disable-prev="disablePrev"
                                :perPage="pagination.itemsPerPage"
                                @rowChanged="fetchDataWithRow"
                                @getCount="getCount"
                                :total="total"
                                :items-loading="totalItemsLoading"
                                :showing-of="showingOf"
            />
            <v-dialog v-model="printDialog" max-width="900" persistent scrollable>
              <offloadPrint
                  v-if="printDialog"
                  :traveller="selectedTraveller"
                  :flight-number="selectedFlight"
                  :airline-name="selectedAirline"
                  :date="selectedDate"
                  @close="printDialog = false"
              />
            </v-dialog>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {mapActions, mapMutations, mapGetters} from 'vuex';
import CustomDateRangePicker from "../../components/CustomDateRangePicker";
import {commonStoreMixin} from "../../mixins/commonStoreMixin";
import IssuedVisaFooter from "../../components/footer/IssuedVisaFooter";
import ExportExcelButton from "../../components/ExportExcelButton";
import FileDisplay from "../../components/FileDisplay";
import RefreshButton from "@/components/RefreshButton";
import OffloadPrint from "@/views/report/generateReport/offload_report.vue";

export default {
  name: "OffloadReport",
  data() {
    return {
      printDialog: false,
      selectedTraveller: null,
      selectedFlight: '',
      selectedAirline: '',
      selectedDate: '',
      pagination: {
        itemsPerPage: 25,
        sortBy: ["created_at"],
        sortDesc: ["true"]
      },
      headers: [
        {
          text: "S.N.",
          value: "s_no",
          sortable: false
        },
        {
          text: "Date",
          value: "created_at",
          sortable: true,
          width: 170
        },
        {
          text: "Photo",
          value: "image",
          sortable: false,
          width: 240
        },
        {
          text: "Traveller Name",
          value: "full_name",
          sortable: false,
          width: 240
        },
        {
          text: "Country",
          value: "country",
          sortable: false
        },
        {
          text: "Document",
          value: "document",
          sortable: false
        },
        {
          text: "Document Number",
          value: "document_number",
          sortable: false
        },
        {
          text: "Reason",
          value: "reason",
          sortable: false
        },
        {
          text: "Offloaded Country",
          value: "offloaded_country",
          sortable: false
        },
        {
          text: "Flight Number",
          value: "flight_number",
          sortable: false
        },
        {
          text: "Offloaded by",
          value: "officer",
          sortable: false,
          width: 240
        },
        {
          text: "Action",
          value: "action",
          sortable: false
        }
      ]
    };
  },
  components: {RefreshButton, FileDisplay, CustomDateRangePicker, ExportExcelButton, IssuedVisaFooter,OffloadPrint},
  mixins: [commonStoreMixin],
  computed: {
    ...mapGetters({
      loading: "OffloadReport/reportLoading",
      visaTypes: "VisaTypeList/all",
      countries: "CountryList/all"
    }),
  },
  methods: {

    ...mapActions({
      fetchCountries: "CountryList/fetchAllCountries"
    }),
    refreshData(){
      this.fetchData();
    },
    openPrintDialog(item) {
      this.selectedTraveller = {
        id: item.traveller,
        name: item.full_name,
        nationality: item.country || item.country_code,
        passport: item.document_number,
        destination: item.offloaded_country,
        reason: item.reason || 'कागजात अपुग भएकोले',
        flight_number:item.flight_number,
        airline_name:item.airline_name,
        officer:item.officer,
        logo: 'https://mofaga.gov.np/images/site_logo.png'
      };
      this.selectedFlight = item.flight_number;
      this.selectedAirline = item.airline || 'Unknown Airline';
      this.selectedDate = item.created_at;

      this.printDialog = true;
    }
  },
  mounted() {
    let params = this.$route.params;
    this.stateName = 'OffloadReport';
    this.commonState = this.$store.state.OffloadReport;
    this.fetchCountries();
    if (params.fromRoute) {
      this.filters.date_range = {"start": params.start_date, "end": params.end_date}
    }
    if (this.$store.state.VisaTypeList.all.length === 2) {
      this.$store.dispatch('VisaTypeList/fetchAll');
    }
  }
}
</script>

<style scoped>

</style>
